import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Shield, 
  Zap, 
  Wrench, 
  CheckCircle2, 
  ChevronDown, 
  Menu, 
  X, 
  Star,
  AlertTriangle,
  Settings,
  Home,
  Award,
  ArrowRight,
  Sparkles,
  CircuitBoard,
  Cable,
  PhoneCall,
  Timer,
  MessageCircle,
  MapPinned,
  ThumbsUp,
  Gauge,
  HelpCircle,
  ChevronRight,
  Play,
  ClipboardList,
  Building2,
  Lightbulb,
  Plug,
  ChevronLeft
} from "lucide-react";

// Logo URL
const LOGO_URL = "/logo.png";
const EMAIL = "palfyvill@gmail.com";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// EmailJS config
const EMAILJS_SERVICE = process.env.REACT_APP_EMAILJS_SERVICE;
const EMAILJS_TEMPLATE = process.env.REACT_APP_EMAILJS_TEMPLATE;
const EMAILJS_KEY = process.env.REACT_APP_EMAILJS_KEY;

const urgencyLabels = { azonnal: "Azonnal / ma", par_nap: "1-2 napon belül", het: "Ezen a héten", rugalmas: "Rugalmas" };
const serviceLabels = { hibaelharitas: "Hibaelhárítás", kapcsolo_konnektor: "Kapcsoló / konnektor", vilagitas: "Világítás szerelés", biztositektabla: "Biztosítéktábla", felujitas: "Felújítás / új hálózat", egyeb: "Egyéb" };
const propertyLabels = { lakas: "Lakás", haz: "Családi ház", iroda: "Iroda / üzlet", tarsashaz: "Társasház közös" };

const sendEmailNotification = (templateParams) => {
  emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, templateParams, EMAILJS_KEY).catch(err => console.error("EmailJS error:", err));
};

// ============================================
// ANIMATED COUNTER
// ============================================
const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// ============================================
// REUSABLE SURVEY FORM (used in Hero + Contact)
// ============================================
const surveyServiceTypes = [
  { value: "hibaelharitas", label: "Hibaelhárítás", desc: "Sürgős javítás, áramszünet", icon: Zap, color: "#FF5722" },
  { value: "kapcsolo_konnektor", label: "Kapcsoló / konnektor", desc: "Csere, javítás, bővítés", icon: Plug, color: "#3B82F6" },
  { value: "vilagitas", label: "Világítás szerelés", desc: "LED, csillár, spotlámpa", icon: Lightbulb, color: "#FF5722" },
  { value: "biztositektabla", label: "Biztosítéktábla", desc: "Csere, bővítés, FI relé", icon: CircuitBoard, color: "#3B82F6" },
  { value: "felujitas", label: "Felújítás / új hálózat", desc: "Komplett villanyszerelés", icon: Home, color: "#FF5722" },
  { value: "egyeb", label: "Egyéb", desc: "Más villanyszerelési igény", icon: Wrench, color: "#3B82F6" },
];
const surveyPropertyTypes = [
  { value: "lakas", label: "Lakás", icon: Home },
  { value: "haz", label: "Családi ház", icon: Home },
  { value: "iroda", label: "Iroda / üzlet", icon: Building2 },
  { value: "tarsashaz", label: "Társasház közös", icon: Building2 },
];
const surveyDetailOptions = [
  "Nincs áram a lakásban", "Kiüt a biztosíték", "Szikrázás / égett szag",
  "Konnektor nem működik", "Forró fal / konnektor", "Új konnektorra van szükség",
  "Világítás nem kapcsol", "Elosztó tábla régi / hibás", "Smart megoldás érdekel", "Kábel cseréje szükséges",
];
const surveyUrgencyOptions = [
  { value: "azonnal", label: "Azonnal / ma", desc: "Sürgős, nem várhat", color: "#FF5722" },
  { value: "par_nap", label: "1-2 napon belül", desc: "Fontos, de nem vészhelyzet", color: "#FF5722" },
  { value: "het", label: "Ezen a héten", desc: "Ráér pár napot", color: "#3B82F6" },
  { value: "rugalmas", label: "Rugalmas", desc: "Nincs sietség, egyeztessünk", color: "#3B82F6" },
];

const useSurvey = () => {
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [data, setData] = useState({
    service_type: "", property_type: "", details: [], extra_note: "",
    urgency: "", name: "", phone: "", email: "", zip_code: ""
  });
  const totalSteps = 5;
  const toggleDetail = (d) => setData(prev => ({ ...prev, details: prev.details.includes(d) ? prev.details.filter(x => x !== d) : [...prev.details, d] }));
  const canProceed = () => {
    if (step === 0) return !!data.service_type;
    if (step === 1) return !!data.property_type;
    if (step === 2) return true;
    if (step === 3) return !!data.urgency;
    if (step === 4) return data.name.trim().length >= 2 && data.phone.replace(/\D/g, "").length >= 6;
    return false;
  };
  const tryNext = () => {
    if (!canProceed()) { setShowErrors(true); return; }
    setShowErrors(false);
    setStep(step + 1);
  };
  const goBack = () => { setShowErrors(false); setStep(step - 1); };
  const handleSubmit = async () => {
    if (!canProceed()) { setShowErrors(true); return; }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/survey`, data);
      sendEmailNotification({
        subject: `Új felmérés: ${serviceLabels[data.service_type] || data.service_type}`,
        from_name: data.name, phone: data.phone, email: data.email || "Nem adta meg",
        zip_code: data.zip_code || "Nem adta meg",
        service_type: serviceLabels[data.service_type] || data.service_type,
        property_type: propertyLabels[data.property_type] || data.property_type,
        urgency: urgencyLabels[data.urgency] || data.urgency,
        details: data.details.length > 0 ? data.details.join(", ") : "Nem jelölt meg semmit",
        extra_note: data.extra_note || "Nincs",
      });
      setSubmitted(true);
    } catch (e) { console.error("Survey error:", e); }
    finally { setIsSubmitting(false); }
  };
  return { step, setStep, data, setData, isSubmitting, submitted, totalSteps, toggleDetail, canProceed, handleSubmit, showErrors, tryNext, goBack };
};

const SurveySteps = ({ survey, prefix = "" }) => {
  const { step, setStep, data, setData, isSubmitting, submitted, totalSteps, toggleDetail, canProceed, handleSubmit, showErrors, tryNext, goBack } = survey;

  const nameInvalid = showErrors && data.name.trim().length < 2;
  const phoneInvalid = showErrors && data.phone.replace(/\D/g, "").length < 6;
  const stepInvalidMsg = showErrors && !canProceed() ? (
    step === 0 ? "Kérjük válasszon munkatípust" :
    step === 1 ? "Kérjük válasszon ingatlantípust" :
    step === 3 ? "Kérjük válassza ki a sürgősséget" :
    step === 4 ? "Kérjük töltse ki a név és telefonszám mezőket" : ""
  ) : "";

  if (submitted) return (
    <div className="text-center py-6">
      <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3"><CheckCircle2 className="w-7 h-7 text-green-400" /></div>
      <h3 className="text-lg font-bold font-['Outfit'] mb-2">Köszönjük!</h3>
      <p className="text-sm text-[#94A3B8] mb-4">Hamarosan keressük pontos árajánlattal.</p>
      <a href="tel:+36708026888" className="btn-primary inline-flex text-sm" data-testid={`${prefix}survey-call-btn`}><Phone className="w-4 h-4 mr-2" />Ha sürgős, hívjon most</a>
    </div>
  );

  return (<>
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 bg-[#FF5722] rounded-xl flex items-center justify-center"><ClipboardList className="w-4 h-4 text-white" /></div>
      <div className="flex-1"><h3 className="text-lg font-bold font-['Outfit']">Gyors felmérés</h3><p className="text-xs text-[#94A3B8]">{step + 1} / {totalSteps} lépés</p></div>
    </div>
    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mb-5">
      <div className="h-full rounded-full transition-all duration-500 led-strip-horizontal" style={{ width: `${((step + 1) / totalSteps) * 100}%` }}></div>
    </div>

    {step === 0 && (<div>
      <h4 className="text-sm font-semibold font-['Outfit'] mb-3">Milyen munkára van szüksége?</h4>
      <div className="grid grid-cols-2 gap-2">
        {surveyServiceTypes.map((s) => (<button key={s.value} onClick={() => setData(prev => ({ ...prev, service_type: s.value }))}
          className={`p-3 rounded-xl border text-left transition-all ${data.service_type === s.value ? 'border-[#FF5722] bg-[#FF5722]/10' : 'border-white/10 bg-[#0A0F1C] hover:border-white/20'}`} data-testid={`${prefix}survey-service-${s.value}`}>
          <s.icon className="w-4 h-4 mb-1" style={{ color: s.color }} /><p className="font-medium text-xs">{s.label}</p><p className="text-[10px] text-[#94A3B8]">{s.desc}</p>
        </button>))}
      </div>
    </div>)}

    {step === 1 && (<div>
      <h4 className="text-sm font-semibold font-['Outfit'] mb-3">Milyen ingatlanról van szó?</h4>
      <div className="grid grid-cols-2 gap-2">
        {surveyPropertyTypes.map((p) => (<button key={p.value} onClick={() => setData(prev => ({ ...prev, property_type: p.value }))}
          className={`p-4 rounded-xl border text-left transition-all ${data.property_type === p.value ? 'border-[#3B82F6] bg-[#3B82F6]/10' : 'border-white/10 bg-[#0A0F1C] hover:border-white/20'}`} data-testid={`${prefix}survey-property-${p.value}`}>
          <p.icon className="w-4 h-4 mb-1 text-[#3B82F6]" /><p className="font-medium text-sm">{p.label}</p>
        </button>))}
      </div>
    </div>)}

    {step === 2 && (<div>
      <h4 className="text-sm font-semibold font-['Outfit'] mb-1">Mi a pontos probléma?</h4>
      <p className="text-[10px] text-[#94A3B8] mb-2">Jelöljön meg mindent, ami releváns (opcionális)</p>
      <div className="grid grid-cols-1 gap-1 max-h-[220px] overflow-y-auto pr-1">
        {surveyDetailOptions.map((d, i) => (<button key={i} onClick={() => toggleDetail(d)}
          className={`flex items-center gap-2 p-2 rounded-lg border text-left text-xs transition-all ${data.details.includes(d) ? 'border-[#FF5722]/50 bg-[#FF5722]/10' : 'border-white/5 bg-[#0A0F1C] hover:border-white/15'}`} data-testid={`${prefix}survey-detail-${i}`}>
          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${data.details.includes(d) ? 'bg-[#FF5722] border-[#FF5722]' : 'border-white/20'}`}>
            {data.details.includes(d) && <CheckCircle2 className="w-2.5 h-2.5 text-white" />}
          </div>{d}
        </button>))}
      </div>
      <textarea value={data.extra_note} onChange={(e) => setData(prev => ({ ...prev, extra_note: e.target.value }))} placeholder="Egyéb megjegyzés..." className="input-custom mt-2 text-xs resize-none" rows={2} data-testid={`${prefix}survey-extra-note`} />
    </div>)}

    {step === 3 && (<div>
      <h4 className="text-sm font-semibold font-['Outfit'] mb-3">Mennyire sürgős?</h4>
      <div className="space-y-2">
        {surveyUrgencyOptions.map((u) => (<button key={u.value} onClick={() => setData(prev => ({ ...prev, urgency: u.value }))}
          className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${data.urgency === u.value ? '' : 'border-white/10 bg-[#0A0F1C] hover:border-white/20'}`}
          style={data.urgency === u.value ? { borderColor: u.color, backgroundColor: `${u.color}15`, borderWidth: '1px', borderStyle: 'solid' } : {}} data-testid={`${prefix}survey-urgency-${u.value}`}>
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: u.color }}></div>
          <div><p className="font-medium text-sm">{u.label}</p><p className="text-[10px] text-[#94A3B8]">{u.desc}</p></div>
        </button>))}
      </div>
    </div>)}

    {step === 4 && (<div>
      <h4 className="text-sm font-semibold font-['Outfit'] mb-3">Elérhetőségei</h4>
      <div className="space-y-3">
        <div><label className="block text-xs text-[#94A3B8] mb-1">Név *</label>
          <input type="text" value={data.name} onChange={(e) => setData(prev => ({ ...prev, name: e.target.value }))} className={`input-custom text-sm ${nameInvalid ? 'border-red-500' : ''}`} placeholder="Az Ön neve" data-testid={`${prefix}survey-name`} />
          {nameInvalid && <p className="text-[10px] text-red-400 mt-1" data-testid={`${prefix}survey-name-error`}>Legalább 2 karakter szükséges</p>}
        </div>
        <div><label className="block text-xs text-[#94A3B8] mb-1">Telefonszám *</label>
          <input type="tel" value={data.phone} onChange={(e) => setData(prev => ({ ...prev, phone: e.target.value }))} className={`input-custom text-sm ${phoneInvalid ? 'border-red-500' : ''}`} placeholder="+36 70 ..." data-testid={`${prefix}survey-phone`} />
          {phoneInvalid && <p className="text-[10px] text-red-400 mt-1" data-testid={`${prefix}survey-phone-error`}>Legalább 6 számjegy szükséges</p>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="block text-xs text-[#94A3B8] mb-1">Email</label>
            <input type="email" value={data.email} onChange={(e) => setData(prev => ({ ...prev, email: e.target.value }))} className="input-custom text-sm" placeholder="opcionális" data-testid={`${prefix}survey-email`} /></div>
          <div><label className="block text-xs text-[#94A3B8] mb-1">Irányítószám</label>
            <input type="text" value={data.zip_code} onChange={(e) => setData(prev => ({ ...prev, zip_code: e.target.value.replace(/\D/g, '').slice(0, 4) }))} className="input-custom text-sm" placeholder="pl. 1134" maxLength={4} data-testid={`${prefix}survey-zip`} /></div>
        </div>
      </div>
    </div>)}

    {stepInvalidMsg && step !== 4 && (
      <p className="text-xs text-red-400 mt-3 text-center" data-testid={`${prefix}survey-step-error`}>{stepInvalidMsg}</p>
    )}

    <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
      {step > 0 ? (<button onClick={goBack} className="flex items-center gap-1 text-[#94A3B8] hover:text-white text-xs transition-colors" data-testid={`${prefix}survey-back`}><ChevronLeft className="w-3 h-3" />Vissza</button>) : <div />}
      {step < totalSteps - 1 ? (
        <button onClick={tryNext} className="btn-primary px-5 py-2 text-xs" data-testid={`${prefix}survey-next`}>Tovább<ArrowRight className="w-3 h-3 ml-1" /></button>
      ) : (
        <button onClick={handleSubmit} disabled={isSubmitting} className="btn-primary px-5 py-2 text-xs disabled:opacity-60" data-testid={`${prefix}survey-submit`}>{isSubmitting ? "Küldés..." : <>Elküldés<ArrowRight className="w-3 h-3 ml-1" /></>}</button>
      )}
    </div>
  </>);
};

const HeroSurveyForm = () => {
  const survey = useSurvey();
  return <SurveySteps survey={survey} prefix="hero-" />;
};

// ============================================
// ============================================
// FLOATING CTA
// ============================================
const FloatingCTA = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="fixed bottom-24 right-6 z-50 flex flex-col items-end gap-3" data-testid="floating-cta">
      {isExpanded && (
        <div className="flex flex-col gap-2 animate-fade-in-up">
          <a href="tel:+36708026888" className="flex items-center gap-2 bg-[#121A2F] border border-white/10 text-white px-4 py-3 rounded-xl hover:border-[#FF5722]/50 transition-all shadow-lg" data-testid="floating-call-btn">
            <Phone className="w-5 h-5 text-[#FF5722]" /><span className="font-medium">Azonnali hívás</span>
          </a>
          <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 bg-[#121A2F] border border-white/10 text-white px-4 py-3 rounded-xl hover:border-[#FF5722]/50 transition-all shadow-lg" data-testid="floating-email-btn">
            <Mail className="w-5 h-5 text-[#FF5722]" /><span className="font-medium">Email küldése</span>
          </a>
        </div>
      )}
      <button onClick={() => setIsExpanded(!isExpanded)} className={`relative w-16 h-16 bg-[#FF5722] rounded-full flex items-center justify-center shadow-lg hover:bg-[#E64A19] transition-all ${isExpanded ? 'rotate-45' : ''}`} data-testid="floating-main-btn">
        {isExpanded ? <X className="w-7 h-7 text-white" /> : <MessageCircle className="w-7 h-7 text-white" />}
      </button>
    </div>
  );
};

// ============================================
// HEADER
// ============================================
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#szolgaltatasok", label: "Szolgáltatások" },
    { href: "#velemenyek", label: "Vélemények" },
    { href: "#ajanlatkeres", label: "Kapcsolat" },
  ];

  return (
    <header data-testid="header" className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? "bg-[#0A0F1C]/95 backdrop-blur-lg border-b border-white/5" : "bg-transparent"}`}>
      <div className="container-custom">
        <div className="flex items-center justify-between h-36">
          <a href="#" className="flex items-center" data-testid="logo">
            <img src={LOGO_URL} alt="PállfyVill" className="h-32 md:h-40 w-auto" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors" data-testid={`nav-${link.href.slice(1)}`}>
                {link.label}
              </a>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-4">
            <a href="tel:+36708026888" className="btn-primary text-sm" data-testid="header-cta-phone">
              <Phone className="w-4 h-4 mr-2" />+36 70 802 6888
            </a>
          </div>
          <button className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-testid="mobile-menu-toggle">
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-[#0A0F1C]/98 z-40 md:hidden pt-24" data-testid="mobile-menu" onClick={() => setIsMobileMenuOpen(false)}>
          <nav className="flex flex-col items-center gap-6 pt-8" onClick={(e) => e.stopPropagation()}>
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} className="text-lg font-medium hover:text-[#FF5722] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                {link.label}
              </a>
            ))}
            <a href="tel:+36708026888" className="btn-primary mt-4" data-testid="mobile-cta-phone" onClick={() => setIsMobileMenuOpen(false)}>
              <Phone className="w-4 h-4 mr-2" />+36 70 802 6888
            </a>
            <button onClick={() => setIsMobileMenuOpen(false)} className="mt-8 text-[#94A3B8] hover:text-white transition-colors text-sm" data-testid="mobile-menu-close">
              Bezárás
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

// ============================================
// HERO SECTION - Redesigned
// ============================================
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0A0F1C]">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url('/hero-bg.png')`,
          backgroundSize: "cover", backgroundPosition: "center"
        }}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1C] via-[#0A0F1C]/90 to-transparent"></div>
      </div>

      <div className="container-custom relative z-10 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="text-[#3B82F6] font-bold uppercase tracking-wider text-sm">Budapest és környéke</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] leading-tight">
              Villanyszerelés<br />
              <span className="text-[#FF5722]">gyorsan</span> és <span className="text-[#3B82F6]">megbízhatóan</span>
            </h1>

            <p className="text-xl text-[#94A3B8] mt-6 max-w-lg">
              0-24 hibaelhárítás, garanciális kivitelezés. Hívjon és <strong className="text-white">1 órán belül</strong> ott vagyunk.
            </p>

            {/* Stats inline */}
            <div className="flex flex-wrap gap-6 mt-8 py-6 border-y border-white/10">
              <div className="text-center">
                <p className="text-3xl font-bold font-['Outfit'] text-[#FF5722]"><AnimatedCounter end={2847} suffix="+" /></p>
                <p className="text-sm text-[#94A3B8]">Elvégzett munka</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold font-['Outfit'] text-[#3B82F6]"><AnimatedCounter end={15} suffix=" év" /></p>
                <p className="text-sm text-[#94A3B8]">Tapasztalat</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold font-['Outfit'] text-white"><AnimatedCounter end={98} suffix="%" /></p>
                <p className="text-sm text-[#94A3B8]">Elégedett ügyfél</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <a href="tel:+36708026888" className="btn-primary text-lg group" data-testid="hero-cta-phone">
                <Phone className="w-5 h-5 mr-2" />
                +36 70 802 6888
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#ajanlatkeres" className="btn-secondary text-lg" data-testid="hero-cta-survey">
                <ClipboardList className="w-5 h-5 mr-2" />Felmérés kitöltése
              </a>
            </div>
          </div>

          {/* Right Content - Inline Survey */}
          <div>
            <div className="survey-highlight" data-testid="hero-survey-wrapper">
              <span className="survey-attention-badge">Ingyenes felmérés</span>
              <span className="survey-bulb tl" aria-hidden="true"></span>
              <span className="survey-bulb tr" aria-hidden="true"></span>
              <span className="survey-bulb bl" aria-hidden="true"></span>
              <span className="survey-bulb br" aria-hidden="true"></span>
              <div className="survey-highlight-inner p-6" data-testid="hero-survey">
                <HeroSurveyForm />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-8 h-8 text-[#94A3B8]" />
      </div>
    </section>
  );
};

// ============================================
// PROBLEMS SECTION - Redesigned
// ============================================
const ProblemSection = () => {
  const problems = [
    { icon: AlertTriangle, title: "Áramszünet", description: "Hirtelen leáll az áram? Gyorsan kiderítjük az okát." },
    { icon: Zap, title: "Zárlat", description: "Veszélyes zárlatok azonnali, biztonságos megszüntetése." },
    { icon: Cable, title: "Régi vezetékek", description: "Korszerűtlen hálózat? Cseréljük, mielőtt baj lesz." },
    { icon: Settings, title: "Biztosíték leverés", description: "Folyamatosan kiüt? Megtaláljuk és javítjuk a hibát." },
  ];

  return (
    <section data-testid="problem-section" className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0D1424 0%, #121A2F 40%, #0F1B35 100%)' }}>
      {/* Visible top divider bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FF5722] via-[#FF5722]/40 to-transparent"></div>
      {/* Diagonal decorative stripe */}
      <div className="absolute top-0 right-0 w-[600px] h-full opacity-[0.035]" style={{ background: 'repeating-linear-gradient(-45deg, transparent, transparent 30px, #FF5722 30px, #FF5722 31px)' }}></div>
      {/* Side accent */}
      <div className="absolute top-[15%] left-0 w-1.5 h-[70%] bg-gradient-to-b from-[#FF5722] via-[#FF5722]/50 to-transparent rounded-r-full"></div>
      
      <div className="container-custom">
        <div className="flex flex-col lg:flex-row gap-12 items-center">
          {/* Left - Text */}
          <div className="lg:w-1/3">
            <span className="text-sm uppercase tracking-[0.2em] text-[#FF5722] font-bold">Ismerős a helyzet?</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mt-4">
              Ne várja meg a <span className="text-[#3B82F6]">nagyobb bajt.</span>
            </h2>
            <p className="text-[#94A3B8] mt-4">
              Ezek a leggyakoribb problémák – és mi mindet azonnal megoldjuk.
            </p>
            <a href="tel:+36708026888" className="btn-primary mt-6 inline-flex">
              <Phone className="w-4 h-4 mr-2" />Hívjon azonnal
            </a>
          </div>

          {/* Right - Cards Grid */}
          <div className="lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {problems.map((problem, index) => (
              <div key={index} className="bg-[#0A0F1C]/80 backdrop-blur-sm border border-white/5 p-6 rounded-2xl hover:border-[#FF5722]/30 transition-all group hover:-translate-y-1" data-testid={`problem-card-${index}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-[#FF5722]/10 border border-[#FF5722]/20 group-hover:bg-[#FF5722]/20 transition-colors">
                  <problem.icon className="w-6 h-6 text-[#FF5722]" />
                </div>
                <h3 className="text-xl font-semibold font-['Outfit'] mb-2">{problem.title}</h3>
                <p className="text-[#94A3B8]">{problem.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// VIDEO SECTION - Bemutatkozás
// ============================================
const VideoSection = () => {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <section data-testid="video-section" className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #080C18 0%, #0A0F1C 50%, #080C18 100%)' }}>
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #3B82F6 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

      <div className="container-custom relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-sm uppercase tracking-[0.2em] text-[#FF5722] font-bold">Ismerjen meg minket</span>
          <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mt-4">Bemutatkozás</h2>
          <p className="text-[#94A3B8] mt-4">Nézze meg, kik állnak a PálffyVill mögött és hogyan dolgozunk.</p>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="video-panel" data-testid="video-player">
            {/* Corner bolts */}
            <span className="video-bolt tl" aria-hidden="true"></span>
            <span className="video-bolt tr" aria-hidden="true"></span>
            <span className="video-bolt bl" aria-hidden="true"></span>
            <span className="video-bolt br" aria-hidden="true"></span>

            {/* Nameplate */}
            <div className="video-nameplate">
              <Zap className="w-3.5 h-3.5 text-[#FF5722]" strokeWidth={2.5} />
              <span className="video-nameplate-text">PállfyVill · CH-01</span>
            </div>

            {/* LED status row */}
            <div className="video-led-row">
              <span className="video-led on-green" aria-hidden="true"></span>
              <span className="video-led-label">PWR</span>
              <span className="video-led on-orange" aria-hidden="true"></span>
              <span className="video-led-label">SIG</span>
              <span className="video-led on-red" aria-hidden="true"></span>
              <span className="video-led-label">REC</span>
            </div>

            {/* Inner screen */}
            <div className="video-screen mt-10">
              <span className="corner-bracket tl" aria-hidden="true"></span>
              <span className="corner-bracket tr" aria-hidden="true"></span>
              <span className="corner-bracket bl" aria-hidden="true"></span>
              <span className="corner-bracket br" aria-hidden="true"></span>

              {!showVideo ? (
                <div className="relative cursor-pointer group" onClick={() => setShowVideo(true)}>
                  <img src="/video_poster.jpg" alt="Bemutatkozó videó" className="w-full" />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <div className="w-20 h-20 bg-[#FF5722] rounded-full flex items-center justify-center shadow-lg shadow-[#FF5722]/30 group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-white ml-1" />
                    </div>
                  </div>
                </div>
              ) : (
                <iframe
                  src="https://drive.google.com/file/d/1aw2_wfzcbh0H_t7NrGuBI7fyi9dCgC3f/preview"
                  className="w-full aspect-[9/16] max-h-[700px]"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  frameBorder="0"
                  title="PálffyVill bemutatkozás"
                  data-testid="video-element"
                ></iframe>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SERVICES SECTION - TURBOCHARGED
// ============================================
const ServicesSection = () => {
  const [activeService, setActiveService] = useState(0);
  
  const services = [
    {
      icon: Zap,
      title: "Hibaelhárítás",
      shortDesc: "0-24 sürgősségi",
      description: "Éjjel-nappal elérhető sürgősségi szolgáltatás. Gyors diagnózis, azonnali javítás. Nem hagyunk cserben!",
      features: ["Azonnali kiszállás", "Zárlat keresés műszerrel", "Biztosíték csere", "Áramszünet elhárítás"],
      highlight: true,
      color: "#FF5722"
    },
    {
      icon: Wrench,
      title: "Általános villanyszerelés",
      shortDesc: "Teljes körű",
      description: "Minden villanyszerelési munka egy kézből. Kapcsolók, konnektorok, világítás - profin és gyorsan.",
      features: ["Kapcsolók, konnektorok", "Világítás szerelés", "Kábel fektetés", "Mérőóra áthelyezés"],
      highlight: false,
      color: "#3B82F6"
    },
    {
      icon: CircuitBoard,
      title: "Új hálózat kiépítés",
      shortDesc: "Tervezés + kivitelezés",
      description: "Korszerű elektromos hálózatok tervezése és kiépítése a legújabb szabványok szerint.",
      features: ["Komplett tervezés", "Engedélyeztetés", "Szakszerű kivitelezés", "Dokumentáció"],
      highlight: false,
      color: "#3B82F6"
    },
    {
      icon: Settings,
      title: "Biztosítéktábla csere",
      shortDesc: "Modernizálás",
      description: "Régi, elavult elosztók cseréje korszerű, biztonságos megoldásokra. FI relé beépítés.",
      features: ["Teljes tábla csere", "FI relé beépítés", "Túlfeszültség védelem", "Bővítés"],
      highlight: false,
      color: "#3B82F6"
    },
    {
      icon: Home,
      title: "Lakásfelújítás",
      shortDesc: "A-tól Z-ig",
      description: "Komplett villanyszerelés felújításokhoz. Tervezéstől a kulcsrakész átadásig.",
      features: ["Teljes átalakítás", "Pontelhelyezés", "Smart otthon", "LED világítás"],
      highlight: false,
      color: "#3B82F6"
    },
  ];

  const activeServiceData = services[activeService];

  return (
    <section id="szolgaltatasok" data-testid="services-section" className="py-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #080C18 0%, #0A0F1C 30%, #0A0F1C 70%, #0E1628 100%)' }}>
      {/* LED pulsing top strip */}
      <div className="absolute top-0 left-0 right-0 h-[3px] led-strip-horizontal z-10"></div>
      {/* Visible grid overlay */}
      <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: 'linear-gradient(rgba(59,130,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px)', backgroundSize: '80px 80px' }}></div>
      {/* Corner glow */}
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-[#3B82F6]/[0.05] rounded-full blur-[120px]"></div>
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-[#FF5722]/[0.04] rounded-full blur-[100px]"></div>

      <div className="container-custom relative z-10">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-sm uppercase tracking-[0.2em] text-[#FF5722] font-bold">Szolgáltatásaink</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] mt-4">
            Miben tudunk <span className="text-[#3B82F6]">segíteni?</span>
          </h2>
        </div>

        {/* Services Layout */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Service Tabs - Left */}
          <div className="lg:col-span-4 space-y-3">
            {services.map((service, index) => (
              <button
                key={index}
                onClick={() => setActiveService(index)}
                className={`w-full text-left p-4 rounded-xl border transition-all duration-300 group ${
                  activeService === index 
                    ? 'bg-[#121A2F] border-[#FF5722]/50 shadow-lg shadow-[#FF5722]/10' 
                    : 'bg-[#121A2F]/50 border-white/5 hover:border-white/20'
                }`}
                data-testid={`service-tab-${index}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                    activeService === index 
                      ? 'bg-[#FF5722] text-white' 
                      : 'bg-white/5 text-[#94A3B8] group-hover:bg-[#FF5722]/20 group-hover:text-[#FF5722]'
                  }`}>
                    <service.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold font-['Outfit'] ${activeService === index ? 'text-white' : 'text-[#94A3B8]'}`}>
                      {service.title}
                    </h3>
                    <p className="text-sm text-[#94A3B8]">{service.shortDesc}</p>
                  </div>
                  {service.highlight && (
                    <span className="px-2 py-1 bg-[#FF5722]/20 text-[#FF5722] text-xs font-bold rounded-full">24/7</span>
                  )}
                  <ChevronRight className={`w-5 h-5 transition-transform ${activeService === index ? 'text-[#FF5722] rotate-90' : 'text-[#94A3B8]'}`} />
                </div>
              </button>
            ))}
          </div>

          {/* Service Detail - Right */}
          <div className="lg:col-span-8">
            <div className="bg-[#121A2F] border border-white/10 rounded-3xl p-8 lg:p-12 h-full relative overflow-hidden">
              {/* Accent line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FF5722] to-[#3B82F6]"></div>
              
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-start gap-6 mb-8">
                  <div className={`w-20 h-20 rounded-2xl flex items-center justify-center`} style={{ backgroundColor: `${activeServiceData.color}20` }}>
                    <activeServiceData.icon className="w-10 h-10" style={{ color: activeServiceData.color }} />
                  </div>
                  <div>
                    <h3 className="text-2xl lg:text-3xl font-bold font-['Outfit']">{activeServiceData.title}</h3>
                    <p className="text-[#94A3B8] mt-2 text-lg">{activeServiceData.description}</p>
                  </div>
                </div>

                {/* Features Grid */}
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {activeServiceData.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-center gap-3 p-4 bg-[#0A0F1C] rounded-xl border border-white/5">
                      <CheckCircle2 className="w-5 h-5 text-[#3B82F6] flex-shrink-0" />
                      <span className="text-[#94A3B8]">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="mt-auto pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-4">
                  <a href="tel:+36708026888" className="btn-primary flex-1 justify-center">
                    <Phone className="w-5 h-5 mr-2" />Hívjon most
                  </a>
                  <a href="#ajanlatkeres" className="btn-secondary flex-1 justify-center">
                    Ajánlatot kérek<ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// ============================================
// TESTIMONIALS SECTION - Redesigned
// ============================================
const TestimonialsSection = () => {
  const testimonials = [
    { name: "Kovács Péter", location: "Budapest, XIII. kerület", rating: 5, text: "Éjszaka ment el nálunk az áram, és egy órán belül itt voltak. Profi, gyors munka, fair ár. Csak ajánlani tudom!", service: "Hibaelhárítás" },
    { name: "Nagy Katalin", location: "Budaörs", rating: 5, text: "Teljes lakásfelújításnál cserélték a villanyvezetéket. Minden a megbeszélt határidőre elkészült, rendesen hagyták a lakást.", service: "Lakásfelújítás" },
    { name: "Szabó Miklós", location: "Budapest, II. kerület", rating: 5, text: "A régi biztosítéktáblánk cseréjét végezték el. Alapos, precíz munka, végig elmagyarázták mit miért csinálnak.", service: "Biztosítéktábla csere" },
  ];

  return (
    <section id="velemenyek" data-testid="testimonials-section" className="py-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #060A14 0%, #0A0F1C 40%, #0D1220 100%)' }}>
      {/* Dot grid background */}
      <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, rgba(59,130,246,0.06) 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
      {/* Large blue glow top-right */}
      <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-[#3B82F6]/[0.06] rounded-full blur-[150px]"></div>
      
      <div className="container-custom">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-sm uppercase tracking-[0.2em] text-[#FF5722] font-bold">Vélemények</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] mt-4">
            Amit <span className="text-[#3B82F6]">ügyfeleink</span> mondanak
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div key={index} className={`border p-8 rounded-2xl hover:border-[#FF5722]/20 transition-all hover:-translate-y-1 relative ${index === 1 ? 'bg-gradient-to-br from-[#121A2F] to-[#0D1424] border-[#FF5722]/10' : 'bg-[#121A2F]/80 border-white/5'}`} data-testid={`testimonial-card-${index}`}>
              {/* Quote mark */}
              <div className="absolute top-6 right-6 text-[#FF5722]/20 text-6xl font-serif">"</div>
              
              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#FF5722] text-[#FF5722]" />
                ))}
              </div>
              
              <p className="text-[#94A3B8] mb-6 leading-relaxed relative z-10">"{testimonial.text}"</p>
              
              <div className="border-t border-white/10 pt-4">
                <p className="font-semibold">{testimonial.name}</p>
                <p className="text-sm text-[#94A3B8]">{testimonial.location}</p>
                <span className="inline-block mt-2 text-xs px-2 py-1 rounded bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/20">
                  {testimonial.service}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Certifications */}
        <div className="mt-12 flex flex-wrap justify-center gap-6">
          <div className="flex items-center gap-2 text-sm text-[#94A3B8] bg-[#121A2F] px-4 py-2 rounded-full border border-white/5">
            <Shield className="w-4 h-4 text-[#FF5722]" />Villanyszerelői jogosítvány
          </div>
          <div className="flex items-center gap-2 text-sm text-[#94A3B8] bg-[#121A2F] px-4 py-2 rounded-full border border-white/5">
            <Award className="w-4 h-4 text-[#3B82F6]" />Felelősségbiztosítás
          </div>
          <div className="flex items-center gap-2 text-sm text-[#94A3B8] bg-[#121A2F] px-4 py-2 rounded-full border border-white/5">
            <CheckCircle2 className="w-4 h-4 text-[#FF5722]" />E-műszaki tanúsítvány
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// PROCESS SECTION - Redesigned
// ============================================
const ProcessSection = () => {
  const steps = [
    { number: "01", title: "Kapcsolatfelvétel", description: "Hívjon vagy írjon, egyeztetjük a részleteket." },
    { number: "02", title: "Helyszíni felmérés", description: "Kimegyünk, felmérjük és árajánlatot adunk." },
    { number: "03", title: "Gyors kivitelezés", description: "Profi eszközökkel, tisztán dolgozunk." },
    { number: "04", title: "Átadás garanciával", description: "Mindent leellenőrzünk, garanciát adunk." },
  ];

  return (
    <section data-testid="process-section" className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0E1628 0%, #131D37 50%, #0E1628 100%)' }}>
      {/* Diagonal lines */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 40px, #FF5722 40px, #FF5722 41px)' }}></div>
      {/* Orange glow bottom-left */}
      <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-[#FF5722]/[0.05] rounded-full blur-[120px]"></div>
      
      <div className="container-custom">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-sm uppercase tracking-[0.2em] text-[#FF5722] font-bold">Folyamat</span>
          <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mt-4">
            Így <span className="text-[#3B82F6]">dolgozunk</span>
          </h2>
        </div>

        <div className="relative">
          {/* Connection line - alternating colors */}
          <div className="hidden lg:block absolute top-12 left-0 right-0 h-0.5">
            <div className="h-full bg-gradient-to-r from-[#FF5722] via-[#3B82F6] to-[#FF5722] opacity-60"></div>
            <div className="absolute top-0 left-0 h-full w-1/4 bg-gradient-to-r from-[#FF5722] to-transparent animate-pulse" style={{animationDuration: '3s'}}></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center" data-testid={`process-step-${index}`}>
                <div className={`w-24 h-24 mx-auto border-2 rounded-2xl flex items-center justify-center mb-6 relative z-10 ${index % 2 === 0 ? 'bg-[#0A0F1C] border-[#FF5722]' : 'bg-[#0A0F1C] border-[#3B82F6]'}`}>
                  <span className={`text-3xl font-bold font-['Outfit'] ${index % 2 === 0 ? 'text-[#FF5722]' : 'text-[#3B82F6]'}`}>{step.number}</span>
                </div>
                <h3 className="text-xl font-semibold font-['Outfit'] mb-2">{step.title}</h3>
                <p className="text-[#94A3B8]">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// CONTACT SECTION - With inline survey (reusable components)
// ============================================
const ContactSection = () => {
  const survey = useSurvey();

  return (
    <section id="ajanlatkeres" data-testid="contact-section" className="py-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #0A0F1C 0%, #080C18 50%, #0A0F1C 100%)' }}>
      <div className="absolute top-0 left-0 right-0 h-[3px] led-strip-horizontal z-10"></div>
      <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'linear-gradient(rgba(255,87,34,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,87,34,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-[#3B82F6]/[0.05] rounded-full blur-[120px]"></div>
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FF5722]/[0.05] rounded-full blur-[120px]"></div>
      
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left - Contact info */}
          <div>
            <span className="text-sm uppercase tracking-[0.2em] text-[#FF5722] font-bold">Kapcsolat</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] mt-4">
              Töltse ki a <span className="text-[#3B82F6]">felmérést!</span>
            </h2>
            <p className="text-[#94A3B8] text-lg mt-6">Pár kattintás, és máris pontos árajánlatot küldünk.</p>
            <div className="mt-10 space-y-4">
              <a href="tel:+36708026888" className="flex items-center gap-4 p-5 bg-[#FF5722]/10 border border-[#FF5722]/20 rounded-2xl hover:bg-[#FF5722]/20 transition-colors group" data-testid="contact-phone-link">
                <div className="w-14 h-14 bg-[#FF5722] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"><Phone className="w-7 h-7" /></div>
                <div><p className="text-sm text-[#94A3B8]">Hívjon most</p><p className="text-2xl font-bold text-[#FF5722]">+36 70 802 6888</p></div>
              </a>
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-4 p-5 bg-[#121A2F] border border-white/10 rounded-2xl hover:border-[#3B82F6]/30 transition-colors" data-testid="contact-email-link">
                <div className="w-14 h-14 bg-[#3B82F6]/20 rounded-xl flex items-center justify-center"><Mail className="w-7 h-7 text-[#3B82F6]" /></div>
                <div><p className="text-sm text-[#94A3B8]">Email</p><p className="font-semibold">{EMAIL}</p></div>
              </a>
              <div className="flex items-center gap-4 p-5 bg-[#121A2F] border border-white/10 rounded-2xl">
                <div className="w-14 h-14 bg-white/5 rounded-xl flex items-center justify-center"><MapPin className="w-7 h-7 text-[#94A3B8]" /></div>
                <div><p className="text-sm text-[#94A3B8]">Szolgáltatási terület</p><p className="font-semibold">Budapest és 30 km-es körzete</p></div>
              </div>
            </div>
          </div>

          {/* Right - Inline Survey */}
          <div className="survey-highlight" data-testid="contact-survey-wrapper">
            <span className="survey-attention-badge">Ingyenes felmérés</span>
            <span className="survey-bulb tl" aria-hidden="true"></span>
            <span className="survey-bulb tr" aria-hidden="true"></span>
            <span className="survey-bulb bl" aria-hidden="true"></span>
            <span className="survey-bulb br" aria-hidden="true"></span>
            <div className="survey-highlight-inner p-8" data-testid="inline-survey">
              <SurveySteps survey={survey} prefix="contact-" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// FOOTER
// ============================================
const Footer = () => {
  const currentYear = new Date().getFullYear();
  const seoLinks = ["Villanyszerelő Budapest", "Villanyszerelő XIII. kerület", "Gyors villanyszerelő", "Sürgős hibaelhárítás", "Villanyszerelés Pest megye"];

  return (
    <footer data-testid="footer" className="bg-[#060A14] border-t border-white/10 py-16 relative overflow-hidden">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <a href="#" className="inline-block mb-4">
              <img src={LOGO_URL} alt="PállfyVill" className="h-32 w-auto" />
            </a>
            <p className="text-[#94A3B8] text-sm">Professzionális villanyszerelés Budapest és környékén. Gyors kiszállás, garanciális munka.</p>
          </div>

          <div>
            <h4 className="font-semibold font-['Outfit'] mb-4">Gyors linkek</h4>
            <ul className="space-y-2 text-sm text-[#94A3B8]">
              <li><a href="#szolgaltatasok" className="hover:text-white transition-colors">Szolgáltatások</a></li>
              <li><a href="#velemenyek" className="hover:text-white transition-colors">Vélemények</a></li>
              <li><a href="#ajanlatkeres" className="hover:text-white transition-colors">Kapcsolat</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold font-['Outfit'] mb-4">Kapcsolat</h4>
            <ul className="space-y-3 text-sm text-[#94A3B8]">
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#FF5722]" /><a href="tel:+36708026888" className="hover:text-[#FF5722]">+36 70 802 6888</a></li>
              <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#3B82F6]" /><a href={`mailto:${EMAIL}`} className="hover:text-[#3B82F6]">{EMAIL}</a></li>
              <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#94A3B8]" />Budapest és környéke</li>
              <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#94A3B8]" />0-24, minden nap</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold font-['Outfit'] mb-4">Szolgáltatási területek</h4>
            <div className="flex flex-wrap gap-2">
              {seoLinks.map((link, index) => (
                <span key={index} className="text-xs text-[#94A3B8] bg-[#121A2F] px-2 py-1 rounded">{link}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-[#94A3B8]">© {currentYear} PállfyVill. Minden jog fenntartva.</p>
          <div className="flex gap-6 text-sm text-[#94A3B8]">
            <a href="/adatvedelmi" className="hover:text-white transition-colors">Adatvédelem</a>
            <span>•</span>
            <a href="/cookie-szabalyzat" className="hover:text-white transition-colors">Cookie szabályzat</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============================================
// MAIN APP
// ============================================
function App() {
  return (
    <div className="App">
      <div className="noise-overlay"></div>
      <Header />
      <main>
        <HeroSection />
        <ProblemSection />
        <VideoSection />
        <ServicesSection />
        <TestimonialsSection />
        <ProcessSection />
        <ContactSection />
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}

export default App;
